import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { Tag } from "../lm-builder";

interface TagMultiSelectProps {
  selectedTags: Tag[];
  availableTags: Tag[];
  onChange: (tags: Tag[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  selectedTags = [],
  availableTags = [],
  onChange,
  placeholder = "Select tags...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isTagSelected = (tagId: number) =>
    selectedTags.some((t) => t.id === tagId);

  const toggleTag = (tag: Tag) => {
    if (isTagSelected(tag.id)) {
      onChange(selectedTags.filter((t) => t.id !== tag.id));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  const removeTag = (tagId: number) => {
    onChange(selectedTags.filter((t) => t.id !== tagId));
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="tag-multi-select" ref={dropdownRef}>
      {/* Selected Tags Display */}
      <div
        className="tag-multi-select-container"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="tag-chips-container">
          {selectedTags.length === 0 && (
            <span className="tag-placeholder">{placeholder}</span>
          )}
          {selectedTags.map((tag) => (
            <span key={tag.id} className="tag-chip">
              {tag.displayLabel || tag.name}
              {!disabled && (
                <button
                  type="button"
                  className="tag-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag.id);
                  }}
                  aria-label="Remove tag"
                >
                  <X size={13} strokeWidth={2.5} />
                </button>
              )}
            </span>
          ))}
        </div>
        <button
          type="button"
          className={`tag-dropdown-toggle${isOpen ? " is-open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            !disabled && setIsOpen(!isOpen);
          }}
          disabled={disabled}
          aria-label="Toggle tag dropdown"
        >
          <ChevronDown size={16} strokeWidth={2.25} className="tag-dropdown-chevron" />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="tag-dropdown-menu">
          {/* Search Input */}
          <div className="tag-search-container">
            <input
              type="text"
              className="tag-search-input"
              placeholder="Search tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          {/* Actions */}
          {selectedTags.length > 0 && (
            <div className="tag-actions">
              <button
                type="button"
                className="tag-action-btn"
                onClick={clearAll}
              >
                Clear All ({selectedTags.length})
              </button>
            </div>
          )}

          {/* Tag List */}
          <div className="tag-list">
            {filteredTags.length === 0 ? (
              <div className="tag-list-empty">No tags found</div>
            ) : (
              filteredTags.map((tag) => (
                <label key={tag.id} className="tag-list-item">
                  <input
                    type="checkbox"
                    checked={isTagSelected(tag.id)}
                    onChange={() => toggleTag(tag)}
                    className="tag-checkbox"
                  />
                  <span className="tag-label">
                    {tag.displayLabel || tag.name}
                    {tag.code && (
                      <span className="tag-code"> ({tag.code})</span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagMultiSelect;
